import React, { useEffect, useState } from "react";
import { ShieldAlert } from "lucide-react";
import PeopleLayout from "../../components/PeopleLayout";
import {
  peopleApi,
  hasCapability,
  formatDetail,
} from "../../lib/api";

const CAP = "attendance.override.create";

export default function PeopleOverrides() {
  const [employees, setEmployees] = useState([]);
  const [employeeId, setEmployeeId] = useState("");
  const [reason, setReason] = useState("Face scan failed");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");
  const [success, setSuccess] = useState(null);

  const allowed = hasCapability(CAP);

  useEffect(() => {
    if (!allowed) return;
    peopleApi.get("/employees").then((r) => setEmployees(r.data)).catch(() => {});
  }, [allowed]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setSuccess(null);
    setSubmitting(true);
    try {
      const r = await peopleApi.post("/people/overrides", {
        employee_id: employeeId,
        reason: notes ? `${reason} — ${notes}` : reason,
        captured_at_iso: new Date().toISOString(),
      });
      setSuccess(r.data);
      setNotes("");
    } catch (e2) {
      setErr(formatDetail(e2.response?.data?.detail) || "Override failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PeopleLayout
      title="Manual Override"
      subtitle="Mark attendance manually · creates a PENDING_REVIEW record"
    >
      {!allowed && (
        <div
          data-testid="pe-overrides-no-cap"
          className="border border-amber-500/40 bg-amber-500/10 p-6 font-mono text-xs text-amber-200"
        >
          Your role does not include <code>attendance.override.create</code>.
          Ask Control Room to grant it. Without this capability you cannot
          create a manual override.
        </div>
      )}

      {allowed && (
        <div className="grid grid-cols-12 gap-6">
          <form
            onSubmit={submit}
            className="col-span-12 md:col-span-7 border border-neutral-800 bg-[#13161C] p-6"
            data-testid="pe-override-form"
          >
            <div className="inline-flex items-center gap-2 px-2 py-1 border border-amber-500 text-amber-300 font-mono text-[10px] uppercase tracking-wider">
              <ShieldAlert size={11} /> capability: {CAP}
            </div>
            <label className="mt-6 block font-mono text-[10px] uppercase tracking-[0.18em] text-neutral-400">
              Employee
            </label>
            <select
              data-testid="pe-override-employee"
              value={employeeId}
              onChange={(e) => setEmployeeId(e.target.value)}
              className="mt-2 w-full h-12 px-3 bg-[#0F1115] border border-neutral-700 focus:border-amber-400 focus:outline-none font-mono"
              required
            >
              <option value="">— Select employee —</option>
              {employees.map((emp) => (
                <option key={emp.id} value={emp.id}>
                  {emp.code} · {emp.name}
                </option>
              ))}
            </select>

            <label className="mt-4 block font-mono text-[10px] uppercase tracking-[0.18em] text-neutral-400">
              Reason
            </label>
            <select
              data-testid="pe-override-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="mt-2 w-full h-12 px-3 bg-[#0F1115] border border-neutral-700 focus:border-amber-400 focus:outline-none font-mono"
            >
              <option>Face scan failed</option>
              <option>Employee not enrolled</option>
              <option>Camera issue</option>
              <option>Shift boundary correction</option>
              <option>Forgotten badge</option>
              <option>Network outage</option>
            </select>

            <label className="mt-4 block font-mono text-[10px] uppercase tracking-[0.18em] text-neutral-400">
              Notes
            </label>
            <textarea
              data-testid="pe-override-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className="mt-2 w-full p-3 bg-[#0F1115] border border-neutral-700 focus:border-amber-400 focus:outline-none font-mono text-sm"
              placeholder="Optional context for the audit ledger…"
            />

            <button
              type="submit"
              data-testid="pe-override-submit"
              disabled={submitting || !employeeId}
              className="mt-6 h-12 px-8 bg-amber-400 text-black font-mono text-sm uppercase tracking-[0.18em] hover:bg-amber-300 disabled:opacity-50"
            >
              {submitting ? "Creating override…" : "Create override"}
            </button>
            {err && (
              <div className="mt-4 text-red-400 font-mono text-sm" data-testid="pe-override-error">
                {err}
              </div>
            )}
          </form>

          <aside className="col-span-12 md:col-span-5">
            <div className="border border-neutral-800 bg-[#13161C] p-5">
              <div className="font-mono text-[10px] uppercase tracking-[0.32em] text-neutral-500">
                Audit envelope
              </div>
              <p className="mt-2 text-xs text-neutral-400 leading-relaxed">
                Every override writes an immutable entry to the People&apos;s Engine
                ledger with actor, role, capability, attendance record, and reason.
              </p>
              <ul className="mt-4 space-y-1 font-mono text-[11px] text-neutral-500">
                <li>actor_user_id</li>
                <li>actor_role</li>
                <li>source = PEOPLE_ENGINE</li>
                <li>capability_used = {CAP}</li>
                <li>attendance_record_id</li>
                <li>previous_payroll_status → PENDING_REVIEW</li>
              </ul>
            </div>

            {success && (
              <div
                className="mt-4 border border-amber-500/60 bg-amber-500/10 p-5"
                data-testid="pe-override-success"
              >
                <div className="font-mono text-[10px] uppercase tracking-[0.32em] text-amber-300">
                  Override recorded
                </div>
                <div className="mt-2 text-sm text-neutral-100">
                  {success.attendance_action} marked for {success.employee_display_name}.
                </div>
                <div className="mt-2 font-mono text-[11px] text-neutral-400">
                  scan_id: {success.scan_id}
                  <br />
                  attendance_record_id: {success.attendance_record_id}
                  <br />
                  payroll_status: <span className="text-amber-300">{success.payroll_status}</span>
                </div>
              </div>
            )}
          </aside>
        </div>
      )}
    </PeopleLayout>
  );
}
